/**
 * The time zones an edition can be run in, and how to show them.
 *
 * **From the runtime's own IANA data (`Intl.supportedValuesOf`)**, like `countries.ts`: the picker
 * and the validator read the same list, and nothing is shipped — every browser and every Node on
 * Lambda carries it already. `UTC` is added because some engines list only geographic zones.
 */

export type TimeZone = {
  /** The IANA name that is stored: `Asia/Jakarta`. */
  id: string
  /** `GMT+07:00` — the offset *now*, so a zone observing summer time shows its current one. */
  offset: string
  /** Minutes east of UTC, for sorting. */
  offsetMinutes: number
}

let cached: readonly TimeZone[] | undefined
let known: ReadonlySet<string> | undefined

function zoneIds(): string[] {
  const listed = Intl.supportedValuesOf('timeZone')
  return listed.includes('UTC') ? listed : ['UTC', ...listed]
}

function offsetOf(zone: string, at: Date): { offset: string; offsetMinutes: number } {
  const part = new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'longOffset' })
    .formatToParts(at)
    .find((p) => p.type === 'timeZoneName')?.value
  // `GMT` alone is how the format spells a zero offset.
  const offset = part === undefined || part === 'GMT' ? 'GMT+00:00' : part
  const match = /^GMT([+-])(\d{2}):(\d{2})$/.exec(offset)
  const minutes =
    match === null ? 0 : (match[1] === '-' ? -1 : 1) * (Number(match[2]) * 60 + Number(match[3]))
  return { offset, offsetMinutes: minutes }
}

/** Every zone, west to east, then by name. Computed once. */
export function timeZones(): readonly TimeZone[] {
  if (cached !== undefined) return cached
  const now = new Date()
  cached = zoneIds()
    .map((id): TimeZone => {
      const { offset, offsetMinutes } = offsetOf(id, now)
      return { id, offset, offsetMinutes }
    })
    .toSorted((a, b) => a.offsetMinutes - b.offsetMinutes || a.id.localeCompare(b.id))
  return cached
}

/** An IANA zone name this runtime lists. Case-sensitive: that is what is stored. */
export function isTimeZone(value: unknown): boolean {
  if (typeof value !== 'string') return false
  known ??= new Set(zoneIds())
  return known.has(value)
}

/**
 * A `datetime-local` value (`2027-03-01T09:00`), read as a wall time **in `zone`**, as an ISO
 * instant carrying that zone's offset: `2027-03-01T09:00:00+07:00`. `null` for anything else.
 *
 * The edition's zone, not the browser's: an operator in Jakarta on a laptop set to UTC, editing a
 * Bali event, means Bali time.
 *
 * The offset depends on the instant being found, so both offsets in force a day either side are
 * tried and the one that maps back to the same wall time wins. Around a summer-time change this is
 * Temporal's `compatible` rule: a repeated hour takes the earlier instant, and a skipped one lands
 * after the gap rather than being refused.
 */
export function toInstant(local: string, zone: string): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(local)
  if (match === null) return null
  const [, year, month, day, hour, minute] = match.map(Number)
  const wall = Date.UTC(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute))
  const DAY = 86_400_000
  const candidates = [wall - DAY, wall + DAY].map(
    (near) => wall - offsetOf(zone, new Date(near)).offsetMinutes * 60_000,
  )
  const exact = candidates.filter(
    (t) => t + offsetOf(zone, new Date(t)).offsetMinutes * 60_000 === wall,
  )
  const at = new Date(exact.length > 0 ? Math.min(...exact) : (candidates[0] ?? wall))
  // Rebuilt from the instant, so the wall time and the offset always agree — even across a gap.
  return `${toLocalInput(at.toISOString(), zone)}:00${offsetOf(zone, at).offset.slice(3)}`
}

/** An instant as the wall time in `zone`, for a `datetime-local` input. Empty if unparseable. */
export function toLocalInput(instant: string, zone: string): string {
  const at = Date.parse(instant)
  if (Number.isNaN(at)) return ''
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-CA', {
      timeZone: zone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    })
      .formatToParts(new Date(at))
      .map((part) => [part.type, part.value]),
  )
  return `${parts['year']}-${parts['month']}-${parts['day']}T${parts['hour']}:${parts['minute']}`
}
